import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Info, Mail, Phone } from 'lucide-react';
import { MAIN_PHONE_DISPLAY, MAIN_PHONE_TEL } from '../../constants/contact';
import { pageMeta } from '../../constants/pageMeta';
import { CALL_NOTE, COMPANY_EMAIL, COMPANY_NAME, SITE_NAME } from '../../constants/site';
import usePageMeta from '../../utils/usePageMeta';
import SiteLayout from '../components/SiteLayout';

const META = pageMeta(
  'Request This Option',
  `Ask a ${SITE_NAME} travel advisor for the live price and availability of a travel option.`,
  '/flight/book'
);

/**
 * Reached from "Request this option" on a sample search result.
 * Nothing is booked or charged here: the visitor emails the option to an advisor,
 * who replies with the live price, rules and service fee.
 */
export default function RequestOptionPage() {
  usePageMeta(META);
  const { state } = useLocation();
  const navigate = useNavigate();
  const [sent, setSent] = useState(false);
  const payload = state?.payload;

  if (!payload?.bookingLines) return <Navigate to="/" replace />;

  const lines = payload.bookingLines.filter((l) => l.value != null && String(l.value).trim() !== '');

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    const body = [
      `Name: ${data.get('name')}`,
      `Email: ${data.get('email')}`,
      `Phone: ${data.get('phone') || '-'}`,
      `Travellers: ${data.get('travellers')}`,
      '',
      'Option I am interested in (sample result from flynodex.com):',
      ...lines.map((l) => `${l.label.replace('Total price', 'Sample price')}: ${l.value}`),
      '',
      `Notes: ${data.get('notes') || '-'}`,
    ].join('\n');
    const subject = `Price request – ${payload.subject || 'Travel option'} – ${data.get('name')}`;
    window.location.href = `mailto:${COMPANY_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setSent(true);
  };

  return (
    <SiteLayout>
      <div className="fx-pagehead">
        <div className="fx-wrap">
          <button type="button" className="fx-backlink" onClick={() => navigate(-1)}>
            <ArrowLeft size={16} aria-hidden /> Back to results
          </button>
          <h1>Request this option</h1>
          <p className="fx-pagehead__lead">
            Send this option to a {SITE_NAME} advisor. We will reply with the live price, the provider’s rules and our
            service fee. Nothing is booked or charged until you approve the final total.
          </p>
        </div>
      </div>

      <div className="fx-wrap fx-request">
        <aside className="fx-request__summary">
          <h2>Your selected option</h2>
          <p className="fx-request__sample">
            <Info size={16} aria-hidden /> This is a sample result for planning. Live prices and availability may be
            different.
          </p>
          <dl>
            {lines.map((l) => (
              <div key={l.label}>
                <dt>{l.label === 'Total price' ? 'Sample price' : l.label}</dt>
                <dd>{l.value}</dd>
              </div>
            ))}
          </dl>
        </aside>

        <form className="fx-form" onSubmit={handleSubmit}>
          <div className="fx-form__grid">
            <label>
              Full name
              <input name="name" required autoComplete="name" />
            </label>
            <label>
              Email
              <input name="email" type="email" required autoComplete="email" />
            </label>
            <label>
              Phone (optional)
              <input name="phone" type="tel" autoComplete="tel" />
            </label>
            <label>
              Travellers
              <input name="travellers" type="number" min="1" max="99" defaultValue="1" />
            </label>
            <label className="fx-form__wide">
              Anything else we should know?
              <textarea name="notes" rows="3" placeholder="Flexible dates, budget, preferred times…" />
            </label>
          </div>
          <label className="fx-form__consent">
            <input type="checkbox" required />
            <span>
              I agree that {COMPANY_NAME} may use these details to reply to my request, as described in the{' '}
              <Link to="/privacy-policy">Privacy Policy</Link> and{' '}
              <Link to="/terms-and-conditions">Terms &amp; Conditions</Link>.
            </span>
          </label>
          <button type="submit" className="fx-btn fx-btn--primary fx-btn--lg">
            <Mail size={18} aria-hidden /> Email my request
          </button>
          <p className="fx-form__note" aria-live="polite">
            {sent
              ? `Your email app should now be open with your request filled in — press send and we will reply, usually within one business day. If nothing opened, email us at ${COMPANY_EMAIL}.`
              : `Submitting opens your email app with a message to ${COMPANY_EMAIL}. We never ask for card details by email.`}
          </p>
          <p className="fx-request__call">
            Prefer to talk? <a href={`tel:${MAIN_PHONE_TEL}`}>
              <Phone size={15} aria-hidden /> {MAIN_PHONE_DISPLAY}
            </a>
            <br />
            <span className="fx-small">{CALL_NOTE}</span>
          </p>
        </form>
      </div>
    </SiteLayout>
  );
}
